import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Car } from "lucide-react";

const emptyForm = { modelo: "", placa: "", tipo: "fixo", tipo_veiculo: "carro", motorista_id: "", motorista_nome: "", ano: "", observacoes: "" };

export default function Vehicles() {
    const [vehicles, setVehicles] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [filterTipo, setFilterTipo] = useState("todos");
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const [v, d] = await Promise.all([api.get("/vehicles"), api.get("/drivers")]);
        setVehicles(v.data);
        setDrivers(d.data);
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
    const openEdit = (v) => {
        setForm({
            modelo: v.modelo, placa: v.placa, tipo: v.tipo, tipo_veiculo: v.tipo_veiculo || "carro",
            motorista_id: v.motorista_id || "", motorista_nome: v.motorista_nome || "",
            ano: v.ano || "", observacoes: v.observacoes || "",
        });
        setEditingId(v.id);
        setOpen(true);
    };

    const selectDriver = (id) => {
        const d = drivers.find((x) => x.id === id);
        setForm({ ...form, motorista_id: id, motorista_nome: d?.nome || "" });
    };

    const save = async () => {
        if (!form.modelo.trim() || !form.placa.trim()) {
            toast.error("Modelo e placa são obrigatórios");
            return;
        }
        const payload = { ...form, motorista_id: form.motorista_id || null };
        try {
            if (editingId) {
                await api.put(`/vehicles/${editingId}`, payload);
                toast.success("Veículo atualizado!");
            } else {
                await api.post("/vehicles", payload);
                toast.success("Veículo cadastrado!");
            }
            setOpen(false);
            load();
        } catch {
            toast.error("Erro ao salvar");
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/vehicles/${id}`);
            toast.success("Veículo removido");
            load();
        } catch {
            toast.error("Erro ao remover");
        } finally {
            setConfirmDelete(null);
        }
    };

    const filtered = filterTipo === "todos" ? vehicles : vehicles.filter((v) => v.tipo === filterTipo);

    return (
        <div className="space-y-6" data-testid="vehicles-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-3xl font-extrabold text-foreground">Veículos</h2>
                    <p className="text-sm text-muted-foreground mt-1">Carros fixos e de diária da cooperativa</p>
                </div>
                <Button onClick={openCreate} data-testid="btn-new-vehicle" className="gap-2">
                    <Plus className="w-4 h-4" /> Novo veículo
                </Button>
            </div>

            <Tabs value={filterTipo} onValueChange={setFilterTipo}>
                <TabsList data-testid="vehicles-tabs">
                    <TabsTrigger value="todos" data-testid="tab-all">Todos</TabsTrigger>
                    <TabsTrigger value="fixo" data-testid="tab-fixo">Contratos</TabsTrigger>
                    <TabsTrigger value="diaria" data-testid="tab-diaria">Diária</TabsTrigger>
                </TabsList>
            </Tabs>

            <Card>
                <CardContent className="p-6">
                    {loading ? (
                        <div className="text-muted-foreground py-8 text-center">Carregando...</div>
                    ) : filtered.length === 0 ? (
                        <div className="py-14 text-center border-2 border-dashed border-border rounded-xl">
                            <Car className="w-10 h-10 mx-auto text-muted-foreground" />
                            <div className="mt-3 font-semibold">Nenhum veículo cadastrado</div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vehicles-grid">
                            {filtered.map((v) => (
                                <Card key={v.id} className="border-border shadow-sm hover:shadow-md transition-shadow">
                                    <CardContent className="p-5">
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${v.tipo === "fixo" ? "bg-primary/10 text-primary" : "bg-accent/25 text-accent-foreground"}`}>
                                                    <Car className="w-5 h-5" />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-foreground">{v.modelo}</div>
                                                    <div className="font-mono text-xs text-muted-foreground">{v.placa}</div>
                                                </div>
                                            </div>
                                            <Badge variant={v.tipo === "fixo" ? "default" : "secondary"} className="uppercase text-[10px]">
                                                {v.tipo === "fixo" ? "Fixo" : "Diária"}
                                            </Badge>
                                        </div>
                                        <div className="mt-4 space-y-1 text-sm">
                                            <div><span className="text-muted-foreground">Motorista:</span> <strong>{v.motorista_nome || "Não atribuído"}</strong></div>
                                            {v.ano && <div><span className="text-muted-foreground">Ano:</span> {v.ano}</div>}
                                            {v.observacoes && <div className="text-xs text-muted-foreground mt-2">{v.observacoes}</div>}
                                        </div>
                                        <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                                            <Button variant="outline" size="sm" onClick={() => openEdit(v)} className="flex-1" data-testid={`btn-edit-vehicle-${v.id}`}>
                                                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                                            </Button>
                                            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(v)} data-testid={`btn-delete-vehicle-${v.id}`}>
                                                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="vehicle-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? "Editar veículo" : "Novo veículo"}</DialogTitle>
                        <DialogDescription>Cadastre um carro fixo ou de diária</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Modelo *</Label>
                                <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} placeholder="Ex: Hilux, Strada" data-testid="input-vehicle-modelo" />
                            </div>
                            <div>
                                <Label>Placa *</Label>
                                <Input value={form.placa} onChange={(e) => setForm({ ...form, placa: e.target.value.toUpperCase() })} placeholder="ABC-1234" data-testid="input-vehicle-placa" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Modalidade *</Label>
                                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                                    <SelectTrigger data-testid="select-vehicle-tipo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="fixo">Fixo</SelectItem>
                                        <SelectItem value="diaria">Diária</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Categoria do veículo *</Label>
                                <Select value={form.tipo_veiculo} onValueChange={(v) => setForm({ ...form, tipo_veiculo: v })}>
                                    <SelectTrigger data-testid="select-vehicle-tipo-veiculo"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="caminhonete">Caminhonete (R$ 12.000/mês)</SelectItem>
                                        <SelectItem value="onibus">Ônibus (R$ 21.000/mês)</SelectItem>
                                        <SelectItem value="carro">Carro</SelectItem>
                                        <SelectItem value="van">Van</SelectItem>
                                        <SelectItem value="caminhao">Caminhão</SelectItem>
                                        <SelectItem value="outro">Outro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Ano</Label>
                                <Input value={form.ano} onChange={(e) => setForm({ ...form, ano: e.target.value })} data-testid="input-vehicle-ano" />
                            </div>
                            <div></div>
                        </div>
                        <div>
                            <Label>Motorista responsável</Label>
                            <Select value={form.motorista_id || "none"} onValueChange={(v) => selectDriver(v === "none" ? "" : v)}>
                                <SelectTrigger data-testid="select-vehicle-driver"><SelectValue placeholder="Selecione" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Nenhum</SelectItem>
                                    {drivers.map((d) => (
                                        <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Observações</Label>
                            <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} data-testid="input-vehicle-obs" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} data-testid="btn-save-vehicle">Salvar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Remover veículo?</AlertDialogTitle>
                        <AlertDialogDescription>O veículo <strong>{confirmDelete?.modelo} ({confirmDelete?.placa})</strong> será removido.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(confirmDelete.id)} data-testid="btn-confirm-delete-vehicle">Remover</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
